import { useEffect, useRef, useCallback } from 'react';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/**
 * Cloudflare Turnstile CAPTCHA widget.
 *
 * Props:
 *   onToken(token)  — called when verification succeeds
 *   onExpire()      — called when token expires (optional)
 *   className       — extra wrapper classes (optional)
 */
export default function TurnstileWidget({ onToken, onExpire, className = '' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || !SITE_KEY) return;

    // Clean up previous widget if any
    if (widgetIdRef.current !== null) {
      try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
    }

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      callback: (token) => onToken?.(token),
      'expired-callback': () => {
        onExpire?.();
        onToken?.(null);
      },
      'error-callback': () => onToken?.(null),
      theme: 'light',
      size: 'flexible',
    });
  }, [onToken, onExpire]);

  useEffect(() => {
    // If turnstile script already loaded
    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Wait for script to load
    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        renderWidget();
      }
    }, 200);

    return () => {
      clearInterval(interval);
      if (widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
      }
    };
  }, [renderWidget]);

  if (!SITE_KEY) return null;

  return <div ref={containerRef} className={className} data-testid="turnstile-widget" />;
}
